export type Locale   = 'fr' | 'en' | 'de' | 'it';
export const LOCALES: readonly Locale[] = ['fr', 'en', 'de', 'it'] as const;
export interface LocaleMeta {
  code: Locale;
  label: string;
  /** ISO 3166-1 alpha-2 region code used for the flag. */
  region: 'FR' | 'GB' | 'DE' | 'IT';
}
export const LOCALE_META: Readonly<Record<Locale, LocaleMeta>> = {
  fr: { code: 'fr', label: 'Français', region: 'FR' },
  en: { code: 'en', label: 'English',  region: 'GB' },
  de: { code: 'de', label: 'Deutsch',  region: 'DE' },
  it: { code: 'it', label: 'Italiano', region: 'IT' },
} as const;
export type BadgeTone    = 'brand' | 'focus' | 'neutral';
export type BadgeVariant = 'solid' | 'outline';

export interface SignalSource { id: string; label: string; sub: string; angle: number; }
export interface SignalItem   { name: string; angle: number; source: string; }
export interface SignalsSectionT {
  eyebrow: string;
  title_part1: string;
  title_accent: string;
  lead: string;
  caption: string;
  center_label: string;
  center_sublabel: string;
  sources: SignalSource[];
  signals: SignalItem[];
}

export interface MegaItem { title: string; desc: string; href: string | null; }
export interface MegaCol  { eye: string; href: string | null; items: MegaItem[]; }

export interface FooterCol        { title: string; links: string[]; hrefs?: (string | null)[]; }
export interface FooterNewsletter { eyebrow: string; title: string; desc: string; placeholder: string; cta: string; caption: string; }

export interface CockpitMetric { key: string; value: string; sub: string; }
export interface VerbT         { mod: string; name: string; desc: string; tag: string; dotColor: string; }
export interface PillarT       { n: string; title: string; desc: string; }
export interface GateT         { key: string; eye: string; badge: string; badgeTone: BadgeTone; inverse: boolean; title: string; lead: string; cta: string; forWho: string; model: string; }
export interface StatT         { value: string; key: string; desc: string; }
export interface VerbatimT     { quote: string; name: string; role: string; }
export interface DiffT         { eyebrow: string; title: string; desc: string; }

export interface Translations {
  header: {
    nav: string[];
    nav_href: (string | null)[];
    mega: MegaCol[];
    cta: string;
    lang: string;
    theme_to_light: string;
    theme_to_dark: string;
  };
  footer: {
    newsletter: FooterNewsletter;
    tagline: string;
    cols: FooterCol[];
    bottom_copy: string;
  };
  home: {
    hero: {
      eyebrow: string;
      title_black: string;
      title_accent: string;
      lead_prefix: string;
      lead_bold: string;
      lead_suffix: string;
      cta_primary: string;
      cta_secondary: string;
      cockpit_suite: string;
      cockpit_date: string;
      cockpit_chart: string;
      cockpit_publi: string;
      metrics: CockpitMetric[];
    };
    s1: { eyebrow: string; title_black: string; title_accent: string; title_suffix: string; lead: string; };
    verbs: VerbT[];
    s2: { eyebrow: string; title_plain: string; title_accent: string; };
    pillars: PillarT[];
    s_signals: SignalsSectionT;
    s3: { eyebrow: string; title_black: string; title_accent: string; lead: string; meta_forwho: string; meta_model: string; };
    gates: GateT[];
    s4: { eyebrow: string; title_black: string; title_accent: string; };
    stats: StatT[];
    logos: string[];
    s5: { eyebrow: string; title_black: string; title_accent: string; };
    verbatims: VerbatimT[];
    s6: { eyebrow: string; title: string; };
    differentiators: DiffT[];
    cta_strip: { eyebrow: string; title_black: string; title_accent: string; cta: string; };
  };
  produit_overview: {
    breadcrumb_home: string;
    breadcrumb_self: string;
    hero: {
      eyebrow: string;
      title_black: string;
      title_accent: string;
      lead_parts: { bold?: string; text: string; }[];
      cta_primary: string;
      cockpit_label: string;
      cockpit_metrics: { key: string; value: string; }[];
      cockpit_chart_label: string;
      cockpit_publi: string;
    };
    manifesto: {
      eyebrow: string;
      intro: string;
      p1: string; sep1: string;
      p2: string; sep2: string;
      p3: string; sep3: string;
      p4: string; outro: string;
      lines: { term: string; desc: string; }[];
    };
    architecture: {
      eyebrow: string;
      title_black: string;
      title_accent: string;
      lead: string;
      meta_forwho: string;
      meta_model: string;
      pillars: ProduitPillarT[];
    };
    proof: {
      eyebrow: string;
      title: string;
      stats: StatT[];
    };
    cta_strip: { eyebrow: string; title_black: string; title_accent: string; cta: string; };
  };
  core_overview: {
    breadcrumb: string[];
    hero: {
      badge: string;
      eyebrow: string;
      title_black: string;
      title_accent: string;
      lead_prefix: string;
      lead_bold: string;
      lead_suffix: string;
      cta_primary: string;
      cta_secondary: string;
      cockpit_label: string;
      cockpit_period: string;
      cockpit_modules: CoreCockpitModule[];
      cockpit_socle_label: string;
      cockpit_socle_chips: string[];
    };
    manifesto: {
      eyebrow: string;
      intro: string;
      p1: string; sep1: string;
      p2: string; sep2: string;
      p3: string; sep3: string;
      p4: string; outro: string;
      lines: CoreManifestoLine[];
    };
    frise: {
      eyebrow: string;
      title: string;
      lead: string;
      phases_legend: CorePhaseLegend[];
      events: CoreFriseEvent[];
      tirage_label: string;
      tirage_sub: string;
      signal_label: string;
      y_axis: string;
      x_axis: string;
      legend: string;
      phases_modules_label: string;
      phases_cards: CorePhaseCard[];
    };
    architecture: {
      eyebrow: string;
      title: string;
      diagram_socle_title: string;
      diagram_socle_desc: string;
      diagram_modules: CoreDiagramModule[];
      meta_forwho: string;
      meta_when: string;
      modules: CoreModuleCard[];
    };
    filRouge: {
      eyebrow: string;
      title: string;
      steps: CoreFilRougeStep[];
      summary_parts: CoreFilRougeSummaryPart[];
    };
    socle: {
      eyebrow: string;
      title: string;
      lead: string;
      items: CoreSocleItem[];
    };
    start: {
      eyebrow: string;
      title: string;
      if_label: string;
      start_label: string;
      items: CoreStartItem[];
      footer_part1: string;
      footer_bold: string;
      footer_part2: string;
    };
    model: {
      eyebrow: string;
      title: string;
      build: { label: string; title: string; desc: string; items: string[]; };
      licence: { label: string; title: string; desc: string; items: string[]; };
    };
    proof: {
      eyebrow: string;
      title: string;
      stats: StatT[];
      verbatims: VerbatimT[];
    };
    cta_strip: { eyebrow: string; title_black: string; title_accent: string; cta: string; };
  };
  /* Print Run overview + Reprint Engine — loose typing since these pages have
     bespoke shapes; the components access nested fields via optional chaining. */
  module_print_run?: Record<string, unknown> & {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
    };
    lifecycle_nodes: string[];
    hub: Record<string, unknown>;
    stats: Record<string, unknown>;
    cta: Record<string, unknown>;
  };
  module_reimpression?: Record<string, unknown> & {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
    };
    mock: Record<string, unknown>;
    problem: Record<string, unknown>;
    produces: Record<string, unknown>;
    rule: Record<string, unknown>;
    monte_carlo: Record<string, unknown>;
    governance: Record<string, unknown>;
    messages: { eyebrow: string; items: string[] };
    limits: { title_part1: string; title_accent: string; title_part2: string; items: string[] };
    cta: Record<string, unknown>;
  };
  module_tirer: {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
      capture_placeholder: string;
    };
    hero_mock: ModuleTirerHeroMock;
    problem: {
      eyebrow: string; title: string;
      para1_part1: string; para1_bold: string; para1_part2: string;
      para2_part1: string; para2_bold: string; para2_part2: string;
      example_eyebrow: string;
      example_part1: string; example_accent: string; example_part2: string;
      example_desc: string;
    };
    f01: {
      eyebrow: string; pill: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      schema_eyebrow: string; schema_legend: string[]; schema_target: string; schema_caption: string;
      inputs_eyebrow: string; input_families: ModuleTirerInputFamily[];
      chips: ModuleTirerChip[];
    };
    f02: {
      eyebrow: string; pill: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string;
      lead_part3: string; lead_bold3: string; lead_part4: string;
      pipeline_label: string; pipeline: ModuleTirerPipelineStep[];
      engines_label: string; engines: ModuleTirerEngine[];
      stats: ModuleTirerStat[];
    };
    f03: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string;
      scenarios_when_label: string;
      scenarios: ModuleTirerScenario[];
      objectives_eyebrow: string; objectives_title: string;
      objectives_lead_part1: string; objectives_lead_bold1: string;
      objectives_lead_part2: string; objectives_lead_bold2: string;
      objectives_lead_part3: string; objectives_lead_bold3: string;
      objectives_lead_part4: string; objectives_lead_bold4: string;
      objectives_lead_part5: string;
      objectives_card_label: string;
      objectives: ModuleTirerObjective[];
      granularity_eyebrow: string; granularity_chips: string[]; granularity_extra: string; granularity_caption: string;
      risk_eyebrow: string; risk_left: string; risk_right: string;
      risk_lead_part1: string; risk_lead_bold: string;
    };
    f04: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      quote_part1: string; quote_accent: string; quote_part2: string;
      drivers_eyebrow: string; drivers: ModuleTirerDriver[];
    };
    f05: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      callout_part1: string; callout_accent: string; callout_part2: string;
      diagram_top_label: string;
      levels: ModuleTirerLevel[];
    };
    messages: { eyebrow: string; items: string[]; };
    limits:   { eyebrow: string; title: string; items: string[]; };
    personas: { eyebrow: string; items: ModuleTirerPersona[]; };
    cta: {
      eyebrow_primary: string;
      title_part1: string; title_accent: string;
      cta: string;
      eyebrow_modules: string;
      modules: ModuleTirerOtherModule[];
    };
  };
  module_ventiler: {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
      capture_placeholder: string;
    };
    hero_mock: {
      url: string;
      topbar_breadcrumb: string[];
      product: {
        title: string;
        status: string;
        price: string;
        author_code: string;
        isbn: string;
        sku: string;
        chips: string[];
      };
      cols: string[];
      rows: ModuleVentilerDistRow[];
    };
    problem: {
      eyebrow: string; title: string;
      para1_part1: string; para1_bold: string; para1_part2: string;
      para2: string;
    };
    steps: {
      eyebrow: string; title: string;
      cascade_total_label: string;
      cascade_total_value: string;
      cascade_step1_label: string;
      cascade_step2_label: string;
      cascade_caption: string;
      channels: ModuleVentilerChannel[];
      step1: {
        eyebrow: string; title: string;
        lead_part1: string; lead_bold1: string;
        lead_part2: string; lead_bold2: string; lead_part3: string;
        channels_eyebrow: string;
      };
      step2: {
        eyebrow: string; title: string;
        lead_part1: string; lead_bold1: string; lead_part2: string;
        hierarchy_eyebrow: string; hierarchy_chips: string[]; hierarchy_caption: string;
        adjust_eyebrow: string;
        adjust_part1: string; adjust_bold: string; adjust_part2: string;
      };
    };
    modes: {
      eyebrow: string; title: string; lead: string;
      items: ModuleVentilerMode[];
    };
    messages: { eyebrow: string; items: string[]; };
    limits:   { eyebrow: string; title: string; items: string[]; };
    personas: { eyebrow: string; items: ModuleTirerPersona[]; };
    cta: {
      eyebrow_primary: string;
      title_part1: string; title_accent: string;
      cta: string;
      eyebrow_modules: string;
      modules: ModuleTirerOtherModule[];
    };
  };
  module_prospecter: {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
      capture_placeholder: string;
    };
    hero_mock: ModuleProspecterHeroMock;
    problem: {
      eyebrow: string; title: string;
      para1_part1: string; para1_bold: string; para1_part2: string;
      para2_part1: string; para2_bold: string; para2_part2: string;
    };
    f01: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      callout_part1: string; callout_accent: string;
      mock_eyebrow: string; mock_status_label: string;
      mock_cols: string[];
      mock_rows: ModuleProspecterMockRow[];
      mock_total_value: string; mock_total_label: string;
      mock_sources_value: string; mock_sources_label: string;
    };
    f02: {
      eyebrow: string; title: string; lead: string;
      indicators: ModuleProspecterIndicator[];
      translation_eyebrow: string;
      translation_part1: string; translation_em1: string;
      translation_part2: string; translation_em2: string;
      translation_part3: string;
    };
    f03: {
      eyebrow: string; title: string; lead: string;
      tag_macro: string; tag_micro: string;
      steps: ModuleProspecterStep[];
    };
    f04: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string;
      lead_part2: string; lead_bold2: string; lead_part3: string;
      callout_part1: string; callout_accent1: string;
      callout_part2: string; callout_accent2: string;
      chart_eyebrow: string; chart_status_label: string;
      chart_y_labels: string[]; chart_x_labels: string[];
      chart_trend_label: string; chart_alert_label: string;
      chart_marker_week: string; chart_caption: string;
    };
    messages: { eyebrow: string; items: string[]; };
    limits:   { eyebrow: string; title: string; items: string[]; };
    personas: { eyebrow: string; items: ModuleTirerPersona[]; };
    cta: {
      eyebrow_primary: string;
      title_part1: string; title_accent: string;
      cta: string;
      eyebrow_modules: string;
      modules: ModuleTirerOtherModule[];
    };
  };
  bookinsight: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      title_part2: string;
      lead: string;
      cta_primary: string;
      cta_signin: string;
      cta_secondary: string;
      privacy_note: string;
      cockpit_label: string;
      cockpit_metrics: { key: string; value: string; }[];
      cockpit_chart_label: string;
    };
    ribbon: {
      tag: string;
      bold: string;
      details: string[];
      cta: string;
    };
    problem: {
      eyebrow: string;
      title: string;
      lead: string;
      pillars: { t: string; d: string; }[];
      callout_value: string;
      callout_text_part1: string;
      callout_text_bold: string;
    };
    flow: {
      eyebrow: string;
      title: string;
      lead: string;
      steps: { icon: string; title: string; sub: string; }[];
    };
    features: {
      eyebrow: string;
      title: string;
      lead: string;
      features_label: string;
      benefits_label: string;
      items: BookInsightFeature[];
    };
    workflow: {
      eyebrow: string;
      title: string;
      lead: string;
      steps: BookInsightWorkflowStep[];
      step_label: string;
    };
    results: {
      eyebrow: string;
      title: string;
      lead: string;
      items: { i: string; t: string; d: string; }[];
    };
    usecases: {
      eyebrow: string;
      title: string;
      lead: string;
      items: { n: string; t: string; d: string; }[];
    };
    personas: {
      eyebrow: string;
      title: string;
      lead: string;
      items: { tag: string; role: string; need: string; }[];
    };
    logos: {
      eyebrow: string;
      items: string[];
    };
    howto: {
      eyebrow: string;
      title: string;
      steps: { n: string; t: string; d: string; }[];
    };
    security: {
      eyebrow: string;
      title: string;
      lead: string;
      cta: string;
      items: string[];
    };
    savings: {
      eyebrow: string;
      title: string;
      lead: string;
      col_task: string;
      col_manual: string;
      col_bi: string;
      rows: { t: string; m: string; b: string; }[];
    };
    faq: {
      eyebrow: string;
      title: string;
      items: { q: string; a: string; }[];
      cta_caption: string;
      cta: string;
    };
    activation: {
      tag: string;
      title_part1: string;
      title_accent: string;
      title_part2: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
      caption: string;
    };
    cta_strip: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
      cta_tertiary: string;
    };
  };
  codex_overview: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      title_part2: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
      pill: string;
      snapshot_label: string;
      snapshot: { l: string; v: string; }[];
    };
    /* 01 — The gap */
    problem: {
      n: string;
      eyebrow: string;
      title_main: string;
      title_muted: string;
      lead: string;
    };
    /* 02 — Conversational intelligence (includes chat mock + 15 lenses inside the same pillar) */
    conversational: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      title_3: string;
      lead: string;
      mock: {
        url: string;
        period: string;
        sidebar_brand: string;
        sidebar_new: string;
        sidebar_today: string;
        sidebar_today_items: string[];
        sidebar_yesterday: string;
        sidebar_yesterday_items: string[];
        ctx: string;
        question: string;
        answer: string;
        metrics: { k: string; v: string }[];
        result_label: string;
        chips: string[];
        suggestions_label: string;
        suggestions: string[];
        input_placeholder: string;
      };
      lenses: {
        kicker: string;
        title_1: string;
        title_grad: string;
        body: string;
        status_labels: { available: string; soon: string; planned: string; };
        groups: CodexLensGroup[];
      };
    };
    /* 03 — Forecasting engine (3 surface tabs, single visualization) */
    forecast: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      lead: string;
      surface_tabs: CodexSurfaceTab[];
      frontlist: CodexFrontlistMock;
      backlist:  CodexBacklistMock;
      portfolio: CodexPortfolioMock;
    };
    /* 04 — Comparables engine */
    comparables: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      title_muted: string;
      lead: string;
      brief_label: string;
      brief: string;
      key_data_label: string;
      key_data_chips: string[];
      ranked_label: string;
      ranked_count: string;
      output_label: string;
      output_text: string;
      center_label: string;
      center_title: string;
      center_author: string;
      center_ini: string;
      center_meta: { k: string; v: string; }[];
      comparables: CodexCompItem[];
      cards: { n: string; d: string; }[];
    };
    /* 05 — One interface (text + 3 mode cards) */
    one_interface: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      body: string;
      modes: CodexModeCard[];
    };
    /* 06 — Under the hood */
    under_hood: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      title_muted: string;
      cards: { t: string; d: string; }[];
    };
    /* Testimonial (no idx) */
    testimonial: {
      kicker: string;
      quote: string;
      name: string;
      role: string;
    };
    /* 07 — Today, in production */
    today: {
      n: string;
      eyebrow: string;
      title_grad: string;
      title_muted: string;
      lead: string;
      stats: { n: string; l: string; sub: string; }[];
    };
    /* 08 — Trajectory (5 phases) */
    trajectory: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      you_are_here: string;
      phases: { p: string; t: string; s: string; current?: boolean; }[];
    };
    /* 09 — Delivery model (Portal vs MCP + 3-layer architecture) */
    delivery: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      lead: string;
      portal: CodexDeliveryPortal;
      mcp: CodexDeliveryMcp;
      arch: CodexDeliveryArch;
    };
    /* 10 — CTA (re-frame from "Why we're talking to you") */
    cta: {
      n: string;
      eyebrow: string;
      title_1: string;
      title_grad: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
      providers_label: string;
      providers: string[];
    };
  };
  insights: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title: string;
      lead: string;
    };
    filters: { key: InsightsCat | 'all'; label: string; }[];
    featured_label: string;
    featured: InsightsArticle;
    articles: InsightsArticle[];
    /** Chrome strings for the article-detail page (/insights/:slug). */
    detail_chrome?: {
      back_to_insights: string;
      read_time_suffix: string;
      published: string;
      share_label: string;
      not_found_title: string;
      not_found_lead: string;
      not_found_cta: string;
    };
    /** Full article bodies keyed by slug. */
    details?: Record<string, InsightArticleDetail>;
  };
  pourquoi_demandsens: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      title_part2: string;
      lead_part1: string;
      lead_bold1: string;
      lead_part2: string;
      lead_bold2: string;
      cta_primary: string;
      cta_secondary: string;
      manifesto_label: string;
      manifesto_part1: string;
      manifesto_accent: string;
      manifesto_caption: string;
    };
    pillars: {
      eyebrow: string;
      title: string;
      lead: string;
      proof_label: string;
      items: PourquoiPillar[];
    };
    contrast: {
      eyebrow: string;
      title: string;
      lead: string;
      not_label: string;
      instead_label: string;
      items: { not: string; why: string; instead: string; }[];
    };
    proof: {
      eyebrow: string;
      title_black: string;
      title_accent: string;
      stats: StatT[];
      logos: string[];
      verbatim_eyebrow: string;
      verbatim_title_black: string;
      verbatim_title_accent: string;
      verbatims: VerbatimT[];
    };
    method: {
      eyebrow: string;
      title: string;
      lead: string;
      timeline: PourquoiTimeline;
      phase_label: string;
      phases: PourquoiPhase[];
      transverse: { t: string; d: string; }[];
    };
    entry: {
      eyebrow: string;
      title: string;
      lead: string;
      meta_who: string;
      meta_what: string;
      meta_when: string;
      cta_label: string;
      items: { n: string; dot: 'core' | 'bi' | 'codex'; sub: string; who: string; what: string; when: string; href: string; }[];
    };
    cta: {
      eyebrow: string;
      title: string;
      lead: string;
      cta_primary: string;
      list_label: string;
      list: string[];
    };
  };
  solutions: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      lead: string;
    };
    jump_label: string;
    pains: SolutionsPain[];
    bottom: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
    };
  };
  bookinsight_faq: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      title_part2: string;
      lead_count_label: string;
      lead_after: string;
      search_placeholder: string;
    };
    sidebar_label: string;
    not_found_t: string;
    not_found_desc: string;
    not_found_cta: string;
    categories: BookInsightFaqCategory[];
    cta: {
      eyebrow: string;
      title: string;
      lead: string;
      cta_primary: string;
      cta_secondary: string;
      cta_tertiary: string;
    };
    related: {
      eyebrow: string;
      items: { t: string; d: string; cta: string; href: string; }[];
    };
  };
  bookinsight_pricing: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      lead: string;
      toggle_monthly: string;
      toggle_annual: string;
    };
    plans: {
      recommended_label: string;
      cta_label: string;
      items: BookInsightPlan[];
    };
    calculator: {
      eyebrow: string;
      title: string;
      lead: string;
      input_label: string;
      manuscripts_label: string;
      pages_label: string;
      pages_min: string;
      pages_max: string;
      manuscripts_min: string;
      manuscripts_max: string;
      options_label: string;
      options: { l: string; cost: string; default: boolean; }[];
      result_label: string;
      tokens_unit: string;
      quota_label: string;
      reco_label: string;
      reco: {
        starter:    { plan: string; desc: string; cta: string; };
        pro:        { plan: string; desc: string; cta: string; };
        enterprise: { plan: string; desc: string; cta: string; };
      };
    };
    packs: {
      eyebrow: string;
      title: string;
      lead: string;
      best_label: string;
      cta_label: string;
      items: { n: string; tk: string; pr: string; perTok: string; use: string; best?: boolean; }[];
    };
    compare: {
      eyebrow: string;
      title: string;
      header_feature: string;
      plans: string[];
      rows: BookInsightCompareRow[];
    };
    faq: {
      eyebrow: string;
      title: string;
      items: { q: string; a: string; }[];
    };
    cta_final: {
      title: string;
      lead: string;
      cta_primary: string;
    };
  };
  bookinsight_security: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_part1: string;
      title_accent: string;
      lead: string;
      pills_strong: string[];
      pills_default: string[];
    };
    engagements: {
      eyebrow: string;
      title: string;
      lead: string;
      items: { n: string; t: string; d: string; tag: string; }[];
    };
    lifecycle: {
      eyebrow: string;
      title: string;
      steps: { n: string; t: string; d: string; icon: string; accent?: boolean; }[];
    };
    measures: {
      eyebrow: string;
      title: string;
      items: { t: string; d: string; icon: string; }[];
    };
    compliance: {
      eyebrow: string;
      title: string;
      lead: string;
      items: { t: string; d: string; }[];
    };
    trust: {
      eyebrow: string;
      title: string;
      items: { t: string; d: string; cta: string; legal?: string; }[];
    };
    contact: {
      eyebrow: string;
      title: string;
      lead: string;
      cta: string;
    };
  };
  module_budgeter: {
    breadcrumb: string[];
    hero: {
      badge: string;
      title_part1: string; title_accent: string; title_part2: string;
      lead: string;
      for_who_label: string; for_who_value: string;
      cta_primary: string; cta_secondary: string;
      capture_placeholder: string;
    };
    problem: {
      eyebrow: string; title: string;
      para1: string; para2: string;
      side_eyebrow: string; side_items: string[];
    };
    c01: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold: string; lead_part2: string;
      steps: ModuleBudgeterStep[];
      capture_placeholder: string;
    };
    c02: {
      eyebrow: string; pill: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      drivers_eyebrow: string; drivers: ModuleBudgeterDriver[];
      schema_eyebrow: string;
      schema_lock_label: string;
      schema_center_title: string; schema_center_sub: string;
      schema_scenarios: ModuleBudgeterScenario[];
      schema_caption: string;
      example_eyebrow: string;
      example_part1_bold: string; example_part2: string; example_quote: string; example_part3: string;
    };
    c03: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold: string; lead_part2: string;
      perspectives: ModuleBudgeterPerspective[];
      toggle_caption: string;
      capture_placeholder: string;
    };
    c04: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      modes_eyebrow: string; modes: string[];
      pyramid_eyebrow: string;
      pyramid_levels: ModuleBudgeterPyramidLevel[];
    };
    c05: {
      eyebrow: string; title: string;
      lead_part1: string; lead_bold1: string; lead_part2: string; lead_bold2: string; lead_part3: string;
      months: string[];
      hc_caption: string;
      legend: ModuleBudgeterLegendItem[];
    };
    c06: {
      eyebrow: string; title: string;
      kpi_eyebrow: string; kpi_axes: string[]; kpi_caption: string;
      exports_eyebrow: string; exports: ModuleBudgeterExport[];
    };
    rbac: {
      eyebrow: string; title: string;
      roles: ModuleBudgeterRole[];
      footer_part1: string; footer_bold: string; footer_part2: string;
    };
    messages: { eyebrow: string; items: string[]; };
    limits:   { eyebrow: string; title: string; items: string[]; };
    personas: { eyebrow: string; items: ModuleTirerPersona[]; };
    cta: {
      eyebrow_primary: string;
      title_part1: string; title_accent: string;
      cta: string;
      eyebrow_modules: string;
      modules: ModuleTirerOtherModule[];
    };
  };
  not_found: {
    eyebrow: string;
    title_part1: string;
    title_accent: string;
    title_part2: string;
    lead: string;
    cta_primary: string;
    cta_secondary: string;
  };
  legal_notice: {
    breadcrumb: string[];
    hero: {
      eyebrow: string;
      title_black: string;
      title_accent: string;
      lead: string;
    };
    company: {
      eyebrow: string;
      title: string;
      items: LegalNoticeItem[];
    };
    representation: {
      eyebrow: string;
      title: string;
      lead: string;
      members: string[];
    };
    contact: {
      eyebrow: string;
      title: string;
      address_label: string;
      address: string;
      phone_label: string;
      phone: string;
      phone_href: string;
      email_label: string;
      email: string;
    };
    notice: {
      eyebrow: string;
      title: string;
      body: string;
    };
  };
  cookies?: CookieConsentT;
}

/* ─── Legal notice types ─────────────────────────────────────── */
export interface LegalNoticeItem { label: string; value: string; }

/* ─── Cookie consent types ──────────────────────────────────────── */
export interface CookieSection {
  n: number;
  title: string;
  intro?: string;
  items?: string[];
  subhead?: string;
  subitems?: string[];
  footnote?: string;
}
export interface CookieConsentT {
  btn_label: string;
  banner: {
    title: string;
    desc: string;
    btn_reject: string;
    btn_customize: string;
    btn_accept: string;
  };
  panel: {
    title_compact: string;
    title_detailed: string;
    more_details: string;
    less_details: string;
    always_active: string;
    optional: string;
    btn_save: string;
    btn_accept: string;
    compact_lead: string;
    compact_summary: string[];
    sections: CookieSection[];
  };
}

export interface ProduitPillarT {
  key: string;
  eye: string;
  badge: string;
  badgeTone: BadgeTone;
  badgeVariant: BadgeVariant;
  inverse: boolean;
  title: string;
  lead: string;
  cta: string;
  href?: string;
  external?: boolean;
  cta_secondary?: string;
  href_secondary?: string;
  forWho: string;
  model: string;
}

/* ─── Core overview types ───────────────────────────────────────── */
export interface CoreCockpitModule { n: string; title: string; value: string; key: string; }
export interface CoreManifestoLine { term: string; desc: string; }
export interface CorePhaseLegend   { label: string; sub: string; }
export interface CoreFriseEvent    { x: number; title: string; sub: string; }
export interface CorePhaseCard     { phase: string; weeks: string; modules: string; desc: string; }
export interface CoreDiagramModule { n: string; title: string; desc: string; }
export interface CoreModuleCard {
  n: string; title: string; quote: string; who: string; when: string; desc: string; cta: string;
}
export interface CoreFilRougeStep {
  h: string; module: string; n: string; who: string; title: string; desc: string;
}
export interface CoreFilRougeSummaryPart { text?: string; accent?: string; }
export interface CoreSocleItem { title: string; desc: string; }
export interface CoreStartItem { question: string; module: string; n: string; why: string; }

/* ─── Module · Tirer types ──────────────────────────────────────── */
export type ModuleTirerChipIcon = 'auto' | 'edit' | 'chart' | 'target';
export interface ModuleTirerChip { label: string; tone?: 'red'; icon?: ModuleTirerChipIcon; }
export interface ModuleTirerInputFamily { title: string; items: string[]; }
export interface ModuleTirerPipelineStep { n: string; title: string; sub: string; }
export interface ModuleTirerEngine { title: string; use: string; link: string; }
export interface ModuleTirerStat { value: string; label: string; }
export interface ModuleTirerScenario { n: string; title: string; repr: string; when: string; tone: 'green' | 'amber' | 'accent'; }
export interface ModuleTirerObjective {
  n: string; title: string;
  desc_part1: string; desc_bold: string;
  lo: string; hi: string; pos: number;
  ex: string;
}
export interface ModuleTirerDriver { name: string; weight: number; }
export interface ModuleTirerLevel { n: number; label: string; sub: string; }
export interface ModuleTirerPersona { tag: string; role: string; need: string; }
export interface ModuleTirerOtherModule { tag: string; name: string; promise: string; }

/* ── Tirer · hero mock (prediction dashboard) ───────────────────── */
export type TirerInfluenceTone =
  | 'frein-fort' | 'frein-significatif' | 'frein-leger'
  | 'boost-modere' | 'boost-significatif';

export interface ModuleTirerHeroMock {
  topbar_breadcrumb: string[];
  product: {
    title: string;
    author: string;
    isbn: string;
    price: string;
    cover_color: string;
    fields: { k: string; v: string }[];
    cta_primary: string;
    cta_secondary: string;
  };
  proposition: {
    eyebrow: string;
    reset: string;
    items: { label: string; value: string; sub?: string; highlighted?: boolean }[];
  };
  influence: {
    title: string;
    items: { label: string; tag: string; value: number }[];
  };
  livraison: {
    title: string;
    items: { label: string; value: string; icon: 'check' | 'cal' | 'clock' }[];
  };
  historique: {
    title: string;
    cols: string[];
    items: ModuleTirerHistEntry[];
  };
  mots_cles: { title: string; chips: string[] };
  resume:    { title: string; text: string };
  score:     { title: string; description: string; value: number; label: string };
}
export interface ModuleTirerHistEntry {
  title: string;
  author: string;
  cover_color: string;
  flux: { k: string; cells: string[] };
  vn:   { k: string; cells: string[] };
  first_print: string;
}

/* ─── Module · Ventiler types ────────────────────────────────────── */
export interface ModuleVentilerChannel { code: string; name: string; ex: string; value: string; }
export interface ModuleVentilerMode    { name: string; desc: string; }
export interface ModuleVentilerDistRow {
  level: number;
  label: string;
  expandable?: boolean;
  italic?: boolean;
  total?: boolean;
  has_note?: boolean;
  v1: string;
  v2: string;
  mod: string;
  obj: string;
}

/* ─── Module · Prospecter types ──────────────────────────────────── */
export interface ModuleProspecterMockRow { kam: string; chain: string; value: string; status: string; tone: 'ok' | 'wait'; }

/* ── Prospecter · hero mock (network performance) ──────────── */
export interface ModuleProspecterHeroMock {
  topbar_breadcrumb: string[];
  settings_label: string;
  side_labels: { columns: string; filters: string };
  tabs: string[];                          // active tab is index 0
  cols_main: string;                       // "Diffuseur / Réseau / Enseigne"
  col_groups: ModuleProspecterColGroup[];
  rows: ModuleProspecterPerfRow[];
}
export interface ModuleProspecterColGroup {
  title: string;                           // "DN", "DV", "Engagement"
  kind: 'pct' | 'num';                     // pct = progress bar, num = numeric
  cols: string[];                          // ["Mois 1", "Mois 2", "Mois 3"]
}
export interface ModuleProspecterPerfRow {
  level: number;                           // 0..3 indent level
  label: string;
  expandable?: boolean;
  italic?: boolean;
  /* Cell values, one entry per column-group, in same order as col_groups.
     Pct cells = number 0..100 ; num cells = string */
  cells: (number | string)[][];
}
export interface ModuleProspecterIndicator {
  code: string; name: string; en: string; desc: string;
  measure_label: string; measure: string; ex: string;
}
export interface ModuleProspecterStep { label: string; value: string; ex: string; tag: '' | 'macro' | 'micro'; }

/* ─── BookInsight types ─────────────────────────────────────────── */
export interface BookInsightFeature {
  n: string;
  t: string;
  d: string;
  features: string[];
  benefits: string[];
  visual: string;
}
export interface BookInsightWorkflowStep {
  t: string;
  m: string;
  highlight?: boolean;
}

/* ─── BookInsight FAQ types ──────────────────────────────────────── */
export type BookInsightFaqCatTone = 'info' | 'positive' | 'warning' | 'focus' | 'accent';
export interface BookInsightFaqQA { q: string; a: string; }
export interface BookInsightFaqCategory {
  id: string;
  label: string;
  desc: string;
  icon: string;
  tone: BookInsightFaqCatTone;
  items: BookInsightFaqQA[];
}

/* ─── Codex types ────────────────────────────────────────────────── */
export type CodexLensStatus = 'available' | 'soon' | 'planned';
export interface CodexLensItem { n: string; s: CodexLensStatus; d: string; }
export interface CodexLensGroup { name: string; items: CodexLensItem[]; }

export type CodexSurfaceKey = 'frontlist' | 'backlist' | 'portfolio';
export interface CodexSurfaceTab { key: CodexSurfaceKey; n: string; t: string; d: string; }

/* One interface mode cards — 3 illustrations */
export type CodexModeViz = 'forecast' | 'comparables' | 'lenses';
export interface CodexModeCard {
  mode: string;
  label: string;
  question: string;
  desc: string;
  pillar: string;
  viz: CodexModeViz;
}

/* Delivery model · Portal mode */
export interface CodexDeliveryPortal {
  label: string;
  title: string;
  sub: string;
  nav_active: string;
  nav: string[];
  brandbar: string;
  card_title: string;
  stats: { n: string; l: string; }[];
  bullets: { b: string; d: string; }[];
}
/* Delivery model · MCP mode */
export interface CodexDeliveryMcp {
  label: string;
  title: string;
  badge: string;
  sub: string;
  copilot_brand: string;
  copilot_meta: string;
  user_who: string;
  user_msg_mention: string;
  user_msg_body: string;
  tool_who: string;
  tool_call: string;
  ai_who: string;
  ai_pill: string;
  ai_intro: string;
  alert_t: string;
  alert_metric_a: string;
  alert_metric_a_value: string;
  alert_metric_a_after: string;
  alert_metric_b: string;
  alert_metric_b_stock: string;
  alert_metric_b_proj: string;
  alert_action: string;
  alert_window: string;
  more_text: string;
  more_link: string;
  bullets: { b: string; d: string; }[];
}
/* Delivery model · 3-layer architecture */
export interface CodexDeliveryArch {
  kicker: string;
  title: string;
  layers: { n: string; t: string; s: string; tag: string; }[];
  wires: string[];
}

export interface CodexBookMeta { k: string; v: string; }
export interface CodexFbookSignal { icon: '↗' | '≈' | '⚠'; label: string; }

export interface CodexFrontlistMock {
  url: string;
  badge: string;
  cover_ini: string;
  cover_color: 'orange' | 'blue' | 'red' | 'purple' | 'cyan' | 'green';
  title: string;
  author: string;
  meta: CodexBookMeta[];
  reco_label: string;
  reco_value: string;
  reco_perf: string;
  reco_perf_value: string;
  reco_conf: string;
  reco_conf_value: string;
  reco_conf_factors: string;
  signals_label: string;
  signals: CodexFbookSignal[];
  chart_label: string;
  chart_tabs: string[];
  chart_marker: string;
}

export interface CodexBacklistMock {
  badge: string;
  cover_ini: string;
  cover_color: 'orange' | 'blue' | 'red' | 'purple' | 'cyan' | 'green';
  title: string;
  author: string;
  meta: CodexBookMeta[];
  reco_label: string;
  reco_action: string;
  reco_value: string;
  reco_value_label: string;
  reco_coverage: string;
  reco_coverage_value: string;
  reco_forecast_label: string;
  reco_forecast_value: string;
  reco_forecast_conf: string;
  signals_label: string;
  signals: CodexFbookSignal[];
  chart_label: string;
  chart_legend: string[];
  chart_marker: string;
}

export interface CodexPortfolioRow {
  ini: string;
  cover_color: 'orange' | 'blue' | 'red' | 'purple' | 'cyan' | 'green';
  title: string;
  author: string;
  status: string;
  sales_1m: string;
  forecast_3m: string;
  coverage: string;
  coverage_alert?: boolean;
  alert: string;
  alert_tone: 'reprint' | 'monitor' | 'watchlist' | 'recommended' | 'noaction';
}

export interface CodexPortfolioMock {
  url: string;
  header_label: string;
  kpis: { k: string; v: string; sub?: string; }[];
  filter_tabs: string[];
  alerts_summary: { label: string; count: string; tone: 'reprint' | 'watchlist'; }[];
  cols: string[];
  rows: CodexPortfolioRow[];
}

export interface CodexCompItem {
  t: string;
  a: string;
  pub: string;
  score: number;
  sales: string;
  src: 'same' | 'group' | 'external';
  ini: string;
  c: 'orange' | 'blue' | 'red' | 'purple' | 'cyan' | 'green';
  /* visual position around center node, in % of container */
  x: number;
  y: number;
}

/* ─── Insights types ─────────────────────────────────────────────── */
export type InsightsCat = 'market' | 'methods' | 'clients' | 'product';
export interface InsightsArticle {
  cat: InsightsCat;
  cat_label: string;
  title: string;
  excerpt?: string;
  read_time: string;
  date: string;
  initials: string;
  cover_color: 'orange' | 'blue' | 'red' | 'purple' | 'cyan' | 'green';
  /** Internal route slug. When set, the card links to /insights/:slug. */
  slug?: string;
  /** External fallback link (used when no slug is provided). */
  href?: string;
  image?: string;
  image_alt?: string;
}

/* ─── Insight article full body — blocks rendered on /insights/:slug ── */
export type InsightBlock =
  | { type: 'p';     text: string }
  | { type: 'h2';    text: string }
  | { type: 'h3';    text: string }
  | { type: 'ul';    items: string[] }
  | { type: 'ol';    items: string[] }
  | { type: 'quote'; text: string; cite?: string }
  | { type: 'video'; provider: 'youtube' | 'vimeo'; id: string; title?: string }
  | { type: 'image'; src: string; alt: string; caption?: string }
  | { type: 'cta';   label: string; href: string };

export interface InsightArticleDetail {
  slug: string;
  cat: InsightsCat;
  cat_label: string;
  title: string;
  excerpt: string;
  read_time: string;
  date: string;
  hero_image: string;
  hero_image_alt: string;
  body: InsightBlock[];
}

/* ─── Pourquoi DemandSens types ──────────────────────────────────── */
export interface PourquoiPillar {
  n: string;
  icon: string;
  t: string;
  d: string;
  proof: string;
}
export interface PourquoiTimeline {
  pilot: { label: string; sub: string; bridge: string; };
  build: { label: string; sub: string; bridge: string; };
  run:   { label: string; sub: string; bridge: string; };
}
export interface PourquoiPhase {
  n: string;
  t: string;
  o: string;
  steps_label: string;
  steps: string[];
  out_label: string;
  out: string;
  tone: 'accent' | 'ink' | 'info';
}

/* ─── Solutions types ────────────────────────────────────────────── */
export interface SolutionsPainProof {
  v: string;
  k: string;
  who: string;
  q: string;
}
export interface SolutionsPain {
  id: string;
  n: string;
  anchor: string;
  pain: string;
  sub: string;
  symptoms_label: string;
  symptoms: string[];
  response_label: string;
  module: string;
  moduleHref: string;
  module_label: string;
  module_cta: string;
  response: string;
  proof: SolutionsPainProof;
  capture: string;
  demo_title: string;
  demo_sub: string;
  demo_cta: string;
}

/* ─── BookInsight pricing/security types ─────────────────────────── */
export interface BookInsightPlan {
  p: string;
  pr: string;
  sub: string;
  d: string;
  cta: string;
  feats: string[];
  primary?: boolean;
}
export interface BookInsightCompareRow {
  sec?: string;
  f?: string;
  vals?: string[];
}

/* ─── Module · Budgeter types ───────────────────────────────────── */
export interface ModuleBudgeterStep   { n: string; title: string; desc: string; }
export interface ModuleBudgeterDriver { n: string; label: string; desc: string; }
export interface ModuleBudgeterScenario { label: string; sub: string; tone: 'green' | 'purple' | 'accent' | 'amber'; }
export interface ModuleBudgeterPerspective { side: 'left' | 'right'; title: string; key: string; desc: string; }
export interface ModuleBudgeterPyramidLevel { label: string; sub: string; }
export interface ModuleBudgeterLegendItem { color: string; label: string; desc: string; }
export interface ModuleBudgeterExport { name: string; desc: string; }
export interface ModuleBudgeterRole { code: string; desc: string; }
